import React from 'react';
import { useTimezone } from '@uplift/contexts';
import { DatePicker, DatePickerProps } from '../../../../Inputs/DatePicker';
import { CalendarDateService } from '@uplift/services';
import { useFormContext } from '../AddSessionForm.types';
import { DateFormats } from '@uplift/constants';
import { CalendarIcon, DatePickerFieldProps, useParsedFormat, usePickerContext, useSplitFieldProps, useValidation, validateDate } from '@mui/x-date-pickers';
import { BaseTextField } from '../../../../Inputs';


const ReadOnlyDateField: React.FC<DatePickerFieldProps> = (props) => {
  const { internalProps, forwardedProps } = useSplitFieldProps(props, 'date');

  const pickerContext = usePickerContext();
  const parsedFormat = useParsedFormat();
  const { hasValidationError } = useValidation({
    validator: validateDate,
    value: pickerContext.value,
    timezone: pickerContext.timezone,
    props: internalProps,
  });

  return (
    <BaseTextField
      {...forwardedProps}
      value={
        pickerContext.value == null
          ? ''
          : pickerContext.value.format(pickerContext.fieldFormat)
      }
      placeholder={parsedFormat}
      slotProps={{
        input: {
          ref: pickerContext.triggerRef,
          readOnly: true,
          endAdornment: <CalendarIcon color="action" />,
          sx: { cursor: 'pointer', '& *': { cursor: 'inherit' } },
        }
      }}
      error={hasValidationError}
      focused={pickerContext.open}
      onClick={() => pickerContext.setOpen((prev) => !prev)}
      className={pickerContext.rootClassName}
      sx={pickerContext.rootSx}
      ref={pickerContext.rootRef}
      name={pickerContext.name}
      label={pickerContext.label}
    />
  );
};


export type AppointmentDateInputProps = {
  name: string;
  label?: string;
  disabled?: boolean;
  TextFieldComponent?: DatePickerProps['TextFieldComponent'];
};


export const AppointmentDateInput: React.VFC<AppointmentDateInputProps> = ({ name, label, disabled, TextFieldComponent = ReadOnlyDateField }) => {
  const timezone = useTimezone();
  const formikContext = useFormContext();

  const minMaxProps = React.useMemo(() => {
    const dt = CalendarDateService.date().tz(timezone);

    return {
      minDate: dt.startOf('day'),
      maxDate: dt.add(180, 'days'),
    };
  }, [ timezone ]);

  const date = formikContext.values.date;
  const startTime = formikContext.values.startTime;

  React.useEffect(() => {
    if (!startTime || !date) {
      return;
    }
    const start = CalendarDateService.date(startTime).tz(timezone);
    const dateObj = CalendarDateService.date.tz(date, DateFormats.ISO_DATE, timezone);
    const dateStr = dateObj.tz(timezone).date();
    const monthStr = dateObj.tz(timezone).month();
    const yearStr = dateObj.tz(timezone).year();

    if (
      dateStr === start.date()
      && monthStr === start.month()
      && yearStr === start.year()
    ) {
      return;
    }

    const newStart = start
      .set('date', dateStr)
      .set('month', monthStr)
      .set('year', yearStr);
    const newStartFormatted = newStart.format();

    formikContext.setFieldValue('startTime', newStartFormatted);
  }, [ date, timezone, startTime ]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DatePicker
      valueFormat={DateFormats.ISO_DATE}
      showKeyboardPickerButton={false}
      disabled={disabled}
      color="secondary"
      slots={{
        field: TextFieldComponent,
      }}
      name={name}
      label={label}
      format="dddd, MMMM D"
      sx={{ minWidth: 230 }}
      {...minMaxProps}
    />
  );
};
